// Paso del proceso con su formulario de evaluación (refleja PasoSerializer).
export type TipoPregunta = "escala" | "opcion" | "texto";

/** Opción de una pregunta de opción única. `valor` es lo que aporta al resultado
 *  cuando esa pregunta es la que lo define ("Aprueba" = 7); null si no calcula nada. */
export interface OpcionPregunta {
  texto: string;
  valor: number | null;
}

export interface PreguntaEvaluacion {
  /** El id importa: las respuestas de la comisión se guardan contra él, así que el
   *  constructor lo devuelve para que editar el formulario no las deje huérfanas. */
  id?: number;
  orden?: number;
  texto: string;
  tipo: TipoPregunta;
  opciones: OpcionPregunta[];
}

// Cómo se agregan las respuestas de la comisión en un resultado (FormularioEvaluacion.Regla).
export type ReglaResultado = "promedio" | "peor" | "mejor";

export const REGLAS_RESULTADO: { value: ReglaResultado; label: string; ayuda: string }[] = [
  { value: "promedio", label: "Promedio de la comisión", ayuda: "Suma las respuestas y las divide." },
  { value: "peor", label: "El peor resultado", ayuda: "Basta que uno reprueba para reprobar." },
  { value: "mejor", label: "El mejor resultado", ayuda: "Basta que uno apruebe para aprobar." },
];

export interface FormularioEvaluacion {
  instrucciones: string;
  preguntas: PreguntaEvaluacion[];
  /** La pregunta cuya respuesta define el resultado del proceso. */
  pregunta_resultado: number | null;
  regla: ReglaResultado;
  regla_display?: string;
}

export interface PasoRef {
  id: number;
  nombre: string;
}

export interface CursoRef {
  id: number;
  codigo: string;
}

// Regla que decide cuándo un estudiante entra a un paso (refleja Paso.ReglaIngreso).
export type ReglaIngreso = "paso_anterior" | "inscripcion_curso" | "manual";

export const REGLAS_INGRESO: { value: ReglaIngreso; label: string; ayuda: string }[] = [
  { value: "paso_anterior", label: "Paso anterior aprobado", ayuda: "Entra al aprobarse su prerrequisito." },
  { value: "inscripcion_curso", label: "Inscrito en el curso", ayuda: "Entra al inscribir el curso (UCampus)." },
  { value: "manual", label: "Manual", ayuda: "Lo agrega el coordinador a mano." },
];

/** Última vez que se pobló el paso en una cohorte (refleja PobladoPaso). */
export interface PobladoPaso {
  ano: number;
  semestre: number;
  periodo: string; // "AAAA.S"
  fecha: string; // ISO 8601
  creadas: number;
  bajas: number;
  por: string | null; // en blanco si lo corrió el comando o el cron
}

export interface Paso {
  id: number;
  nombre: string;
  tamano_comision: number; // evaluadores que asigna el algoritmo (sin contar guía/coguía)
  incluir_guia: boolean; // el guía integra la comisión (se suma al total)
  incluir_coguia: boolean; // el coguía integra la comisión (se suma al total)
  optimizar_areas: boolean; // priorizar afinidad de áreas docente↔tema
  regla_ingreso: ReglaIngreso; // cómo se puebla el paso con estudiantes
  regla_ingreso_display: string;
  cursos: CursoRef[]; // cursos que gatillan el ingreso (regla inscripcion_curso)
  formulario: FormularioEvaluacion;
  depende_de: PasoRef[]; // prerrequisitos
  conduce_a: PasoRef[]; // pasos siguientes
  poblados: PobladoPaso[]; // una por cohorte, de la más nueva a la más vieja
}

// Parámetros editables por el coordinador (PATCH parcial).
export interface PasoParamsInput {
  tamano_comision?: number;
  incluir_guia?: boolean;
  incluir_coguia?: boolean;
  optimizar_areas?: boolean;
  regla_ingreso?: ReglaIngreso;
}

// Poblado de un paso por su regla de ingreso (refleja PoblarPasoView).
export interface PoblarPayload {
  paso: number;
  ano: number;
  semestre: number;
  commit?: boolean;
  dar_de_baja?: boolean; // además, dar de baja a los que ya no están en el curso
  ruts?: number[]; // a quiénes incluir; sin esto, se omite a quien tenga un proceso vigente
}

/** Proceso vigente que la persona ya tiene en otro paso de la misma cohorte. */
export interface ProcesoVigente {
  paso: string;
  estado: string;
}

export interface FilaPoblado {
  rut: number;
  nombre: string;
  origen: string; // paso prerrequisito o código de curso
  conflicto: ProcesoVigente | null; // si ya está en otro paso: traerlo lo duplicaría
}

export interface ResultadoPoblado {
  regla: ReglaIngreso;
  creadas: FilaPoblado[];
  bajas: FilaPoblado[]; // ya no inscritos: quedan en estado "eliminado"
  cierres: FilaPoblado[]; // el curso ya tiene resultado en UCampus; `origen` es el estado nuevo
  avisos: string[];
  publicado: boolean;
}

/** Proceso candidato a moverse de paso (coordinador). */
export interface CandidatoMover {
  instancia_id: number;
  rut: number;
  nombre: string;
  estado: string;
  arrastra: string[]; // documento / comisión / evaluaciones que se irían con él
}

export interface MoverPayload {
  paso_destino: number;
  instancias: number[];
  commit?: boolean;
}
